import DeleteConfirmationDialog from '@components/DeleteConfirmation';
import { useFetchStudyTags } from '@hooks/getQueries';
import {
  useAddNewParticipant,
  useAddStudyTag,
  useAssignUnAssignTagInTranscript,
  useUpdateSpeaker,
  useUpdateSpeakerInfo,
} from '@hooks/postQueries';
import useConfirmationDialog from '@hooks/useDeleteConfirmation';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { Box, Button, Divider, Popover, Stack, TextField, Typography } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import formatString from '@utils/formatString';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import AssignTags from '../Tag/AssignTags';
import UpdateRoleConfirmation from './UpdateRoleConfirmation';

const participantList = [
  {
    id: 1,
    name: 'Elizabeth Douglus Homes',
  },
  {
    id: 2,
    name: 'Maya',
  },
];
function flattenAssignedTags(assigned_tags) {
  return assigned_tags.flatMap((tag) =>
    tag.values.map((value) => ({
      tag_key_id: tag.tag_key_id,
      tag_key: tag.tag_key,
      tag_value_id: value.tag_value_id,
      value_key: value.value_key,
    })),
  );
}

function EditInterviewer({
  openPopoverInterview,
  setOpenPopoverInterview,
  selectedItem,
  id,
  speakers,
  transcriptId,
  canEdit,
}) {
  const [anchorPosition, setAnchorPosition] = useState(null);
  const [newParticipantAnchor, setNewParticipantAnchor] = useState(null);
  const [mergeParticipantAnchor, setMergeParticipantAnchor] = useState(null);
  const { mutateAsync: updateSpeaker, isPending: isUpdatingSpeaker } = useUpdateSpeaker();
  const { open, show, handleConfirm, handleClose } = useConfirmationDialog();
  const { mutateAsync, isPending } = useAssignUnAssignTagInTranscript('unassign');
  const [previouslyAssignedTags, setpreviouslyAssignedTags] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const { data: tagsList, isPending: isFetchingTags } = useFetchStudyTags({
    studyId: id,
    searchQuery,
  });
  const { mutateAsync: updateSpeakerInformation, isPending: isUpdatingSpeakerInfo } =
    useUpdateSpeakerInfo();
  const [speakerItem, setSpeakerItem] = useState(null);
  const [participantName, setParticipantName] = useState('');
  const [isEmptyParticipantName, setIsEmptyParticipantName] = useState(false);
  const [openAssignTagModal, setOpenAssignTagModal] = useState(false);

  const { mutateAsync: addStudyTag, isPending: isAddingStudyTag } = useAddStudyTag();

  const {
    control: speakerControl,
    handleSubmit: speakerHandleSubmit,
    setValue: setSpeaker,
  } = useForm({
    defaultValues: {
      name: '',
      role: '',
      id: '',
    },
  });
  const [currentSpeaker, setCurrentSpeaker] = useState(null);
  const [showUpdateRoleConfirmation, setShowUpdateRoleConfirmation] = useState(false);

  const handleCloseAssignTagModal = () => {
    setOpenAssignTagModal(false);
  };

  useEffect(() => {
    let speaker = speakers?.find((item) => item?.id === selectedItem?.speaker_id);

    setSpeakerItem(speaker);
    setCurrentSpeaker({
      name: speaker?.name,
      role: speaker?.role,
      speaker_id: speaker?.id,
      turn_id: selectedItem?.id,
    });
    setSpeaker('name', speaker?.name);
    setSpeaker('role', speaker?.role);
    setSpeaker('id', selectedItem?.id);
  }, [selectedItem]);

  useEffect(() => {
    let speaker = speakers?.find((item) => item?.id === currentSpeaker?.speaker_id);

    setSpeakerItem(speaker);
  }, [speakers, currentSpeaker]);

  const handleClosePopover = () => {
    setOpenPopoverInterview(null);
  };

  const handleCloseChildPopover = () => {
    setAnchorPosition(null);
  };

  const handleCloseMergeParticpantPopover = () => {
    setMergeParticipantAnchor(null);
  };

  const handleCloseNewParticipantPopover = () => {
    setNewParticipantAnchor(null);
  };

  const { mutate: addNewParticipant, isPending: isAddingNewParticipant } = useAddNewParticipant(
    handleCloseNewParticipantPopover,
  );

  const handleOpenChildPopover = (e) => {
    const target = e.currentTarget;

    if (!target) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setAnchorPosition({
      top: rect?.top,
      left: rect?.left + 54,
    });
  };

  const handleOpenNewParticipnatPopover = (e) => {
    const target = e.currentTarget;

    if (!target) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setNewParticipantAnchor({
      top: rect?.top,
      left: rect?.left + 31,
    });
  };

  const handleSpeakerUpdate = async () => {
    let payload = {
      id: currentSpeaker.turn_id.toString(),
      speaker_id: currentSpeaker?.speaker_id,
    };

    try {
      const res = await updateSpeaker({ payload, transcriptId });
      if (res) {
        setCurrentSpeaker(null);
        setShowUpdateRoleConfirmation(false);
        handleClosePopover();
      }
    } catch (error) {
      console.error('Failed to add code. Please try again.', error?.detail || error);
    }
  };

  const handleCloseConfirmation = () => {
    let speaker = speakers?.find((item) => item?.id === selectedItem?.speaker_id);

    setSpeakerItem(speaker);
    setCurrentSpeaker({
      name: speaker?.name,
      role: speaker?.role,
      speaker_id: speaker?.id,
      turn_id: selectedItem?.id,
    });
    setSpeaker('name', speaker?.name);
    setSpeaker('role', speaker?.role);
    setSpeaker('id', selectedItem?.id);
    setShowUpdateRoleConfirmation(false);
  };

  const updateSpeakerInfo = async (data) => {
    let payload = {
      name: data?.name,
      role: data.role,
      id: selectedItem?.id.toString(),
    };
    try {
      const response = await updateSpeakerInformation({
        payload,
        transcriptId,
        speakerId: currentSpeaker?.speaker_id,
      });
      if (response) {
        handleCloseChildPopover();
      }
    } catch (error) {
      console.error('Failed to add code. Please try again.', error?.detail || error);
    }
  };

  const handleAddNewParticipant = (e) => {
    e.preventDefault();
    if (participantName.length <= 0) {
      setIsEmptyParticipantName(true);
    }
    let payload = {
      name: participantName,
      role: 'participant',
      id: currentSpeaker?.turn_id.toString(),
    };

    addNewParticipant({ payload, transcriptId });
    setParticipantName('');
  };

  const handleDeleteTag = useCallback(
    (tagPayload) => {
      show(async () => {
        const payload = {
          tags: tagPayload,
          isAssignTag: false,
        };

        let finalPayload = {
          payload,
          transcriptId,
          studyId: id,
          speakerId: currentSpeaker?.speaker_id,
        };

        mutateAsync(finalPayload);
      });
    },
    [mutateAsync, show],
  );
  const open1 = Boolean(openPopoverInterview) || false;
  const pop1Id = open1 ? 'simple-popover' : undefined;
  const open2 = Boolean(anchorPosition);
  const pop2Id = open2 ? 'simple-popover' : undefined;
  const openMergeParticpantPopover = Boolean(mergeParticipantAnchor);
  const openMergeParticipantPopoverId = openMergeParticpantPopover ? 'simple-popover' : undefined;
  const openCreateParticipantPopover = Boolean(newParticipantAnchor);
  const openCreateParticipantPopoverId = openCreateParticipantPopover
    ? 'simple-popover'
    : undefined;

  return (
    <>
      <Popover
        id={pop1Id}
        open={open1}
        anchorEl={openPopoverInterview}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box>
          <Box sx={{ paddingX: '5px', marginTop: '5px' }}>
            <FormControl sx={{ p: 1 }}>
              <FormLabel id="demo-radio-buttons-group-label" sx={{ color: '#6B6B6B' }}>
                ID: {speakerItem?.id}
              </FormLabel>
              <RadioGroup
                aria-labelledby="demo-radio-buttons-group-label"
                defaultValue="female"
                name="radio-buttons-group"
              >
                {speakers?.map((item) => (
                  <Box width="100%" key={item?.id} mt={0.5}>
                    <FormControlLabel
                      value={item.role}
                      sx={{
                        backgroundColor: currentSpeaker?.speaker_id == item.id ? '#f2f2f2' : '',
                        '&:hover': { backgroundColor: '#f2f2f2' },
                        cursor: 'pointer',
                        marginLeft: '-4px',
                        borderRadius: '4px',

                        display: 'flex',
                        width: '100%',
                        alignItems: 'center',
                      }}
                      control={
                        <Radio
                          checked={currentSpeaker?.speaker_id == item.id}
                          onClick={() => {
                            if (currentSpeaker?.speaker_id !== item.id) {
                              setCurrentSpeaker({
                                ...currentSpeaker,
                                name: item?.name,
                                role: item?.role,
                                id: item?.id,
                                speaker_id: item?.id,
                              });
                              setSpeaker('name', item?.name);
                              setSpeaker('role', item?.role);
                              setShowUpdateRoleConfirmation(true);
                            }
                          }}
                        />
                      }
                      label={
                        <Box
                          sx={{
                            flex: 1,
                            display: 'flex',
                          }}
                        >
                          <Box
                            paddingY={'2px'}
                            display={'flex'}
                            alignItems={'center'}
                            justifyContent={'space-between'}
                            flexGrow={1}
                            width="220px"
                          >
                            <Box
                              onClick={() => {
                                if (currentSpeaker?.speaker_id !== item.id) {
                                  setCurrentSpeaker({
                                    ...currentSpeaker,
                                    name: item?.name,
                                    role: item?.role,
                                    id: item?.id,
                                    speaker_id: item?.id,
                                  });
                                  setSpeaker('name', item?.name);
                                  setSpeaker('role', item?.role);
                                  setShowUpdateRoleConfirmation(true);
                                }
                              }}
                            >
                              <Typography variant="subtitle2" sx={{ fontSize: '14px' }}>
                                {formatString(item?.name)}
                              </Typography>
                              <Typography sx={{ fontSize: '12px' }}>
                                {' '}
                                Role : {formatString(item?.role)}
                              </Typography>
                            </Box>
                            {currentSpeaker?.speaker_id == item.id && (
                              <Typography
                                sx={{
                                  bgcolor: '#D9D9D9',
                                  display: 'flex',
                                  alignItems: 'center',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  padding: '4px',
                                }}
                                onClick={(e) => handleOpenChildPopover(e)}
                                aria-label="More options"
                              >
                                <MoreHorizIcon />
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      }
                    />
                  </Box>
                ))}
              </RadioGroup>
            </FormControl>
            <Divider sx={{ bgcolor: '#E8E8E8' }} />
            <Box
              display={'flex'}
              alignItems={'center'}
              justifyContent={'space-between'}
              paddingY={1}
              margin={0.6}
              sx={{ '&:hover': { backgroundColor: '#f2f2f2', cursor: 'pointer' } }}
              onClick={() => {
                setOpenAssignTagModal(true);
                setpreviouslyAssignedTags(speakerItem?.assigned_tags);
              }}
            >
              <Typography sx={{ px: '10px' }}>Assign Tag</Typography>
              <KeyboardArrowRightIcon sx={{ color: '#303030' }} />
            </Box>
            {currentSpeaker?.role?.toLowerCase() === 'participant' && (
              <Box
                display={'flex'}
                alignItems={'center'}
                justifyContent={'space-between'}
                paddingY={1}
                margin={0.6}
                sx={{ '&:hover': { backgroundColor: '#f2f2f2' } }}
              >
                <Typography sx={{ px: '10px' }}>Create New Participant</Typography>
                <KeyboardArrowRightIcon
                  onClick={(e) => handleOpenNewParticipnatPopover(e)}
                  sx={{ color: '#303030', cursor: 'pointer' }}
                />
              </Box>
            )}

            <Divider sx={{ bgcolor: '#E8E8E8' }} />
            <Box p={1}>
              <Typography sx={{ fontWeight: '600' }}>Tags</Typography>
              <Box mt={1} mb={1}>
                <Box sx={{ width: '250px', display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                  {speakerItem?.assigned_tags?.length > 0 ? (
                    flattenAssignedTags(speakerItem?.assigned_tags).map((item) => (
                      <Typography
                        display={'flex'}
                        alignItems={'center'}
                        padding={'4px 8px'}
                        bgcolor={'#F2F2F2'}
                        maxWidth={'min-content'}
                        borderRadius={'24px'}
                        fontSize={12}
                        whiteSpace={'nowrap'}
                        key={item?.tag_key_id}
                      >
                        {formatString(item?.tag_key)}
                        <Typography
                          component="span"
                          sx={{ fontWeight: 'bold', marginLeft: '4px', whiteSpace: 'nowrap' }}
                          fontSize={12}
                        >
                          {formatString(item?.value_key)}
                        </Typography>{' '}
                        <CloseIcon
                          fontSize="12"
                          sx={{ marginLeft: '8px', cursor: 'pointer' }}
                          onClick={() => {
                            let tempTag = [
                              {
                                tag_key_id: item.tag_key_id,
                                tag_value_id: item.tag_value_id,
                              },
                            ];
                            handleDeleteTag(tempTag);
                          }}
                        />
                      </Typography>
                    ))
                  ) : (
                    <Typography textAlign={'center'} width={'100%'} p={1}>
                      No Tags
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Popover>
      <UpdateRoleConfirmation
        open={showUpdateRoleConfirmation}
        onClose={handleCloseConfirmation}
        onConfirm={handleSpeakerUpdate}
        isPending={isUpdatingSpeaker}
        currentSpeaker={currentSpeaker}
      />
      <Popover
        id={open2}
        open={pop2Id}
        anchorPosition={anchorPosition}
        anchorReference="anchorPosition"
        onClose={handleCloseChildPopover}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <form onSubmit={speakerHandleSubmit(updateSpeakerInfo)}>
          <Box sx={{ paddingX: '5px', marginTop: '5px', width: '250px' }}>
            <Typography p={1}>
              <span style={{ color: 'gray' }}>Info :</span> You can change name and role by clicking
              Save Changes Button
            </Typography>
            <Divider />
            <Stack sx={{ p: 1.5 }} direction="column" fullWidth>
              <FormLabel>Edit Name</FormLabel>
              <Controller
                name="name"
                control={speakerControl}
                render={({ field }) => (
                  <TextField {...field} variant="outlined" size="small" fullWidth />
                )}
              />
            </Stack>
            <FormControl sx={{ p: 1.5 }}>
              <FormLabel id="demo-radio-buttons-group-label" sx={{ color: '#6B6B6B' }}>
                Select Role
              </FormLabel>
              <Controller
                name="role"
                control={speakerControl}
                render={({ field }) => (
                  <RadioGroup {...field} aria-labelledby="role-radio-label">
                    <FormControlLabel
                      value="interviewer"
                      sx={{
                        '&:hover': { backgroundColor: '#f2f2f2', width: '228px' },
                        cursor: 'pointer',
                      }}
                      control={<Radio />}
                      label="Interviewer"
                    />
                    <FormControlLabel
                      value="participant"
                      sx={{
                        '&:hover': { backgroundColor: '#f2f2f2', width: '228px' },
                        cursor: 'pointer',
                      }}
                      control={<Radio />}
                      label="Participant"
                    />
                  </RadioGroup>
                )}
              />
            </FormControl>
            {/* {selectedItem?.role === 'participant' && (
              <>
                <Divider />
                <Box
                  display={'flex'}
                  justifyContent={'space-between'}
                  alignItems={'center'}
                  margin={0.6}
                  sx={{
                    '&:hover': { backgroundColor: '#f2f2f2' },
                    paddingY: '5px',
                    paddingLeft: '10px',
                    cursor: 'pointer',
                  }}
                  onClick={(e) => handleOpenMergeParticipantPopover(e)}
                >
                  <Typography>Merge Participant</Typography>
                  <KeyboardArrowRightIcon sx={{ cursor: 'pointer', color: '#303030' }} />
                </Box>
              </>
            )} */}

            <Stack sx={{ padding: '5px', marginBottom: '10px' }}>
              <Button
                disabled={isUpdatingSpeakerInfo}
                sx={{ cursor: 'pointer' }}
                type="submit"
                variant="contained"
              >
                {isUpdatingSpeakerInfo ? 'Saving' : 'Save Changes'}
              </Button>
            </Stack>
          </Box>
        </form>
      </Popover>

      <Popover
        id={openMergeParticipantPopoverId}
        open={openMergeParticpantPopover}
        anchorPosition={mergeParticipantAnchor}
        anchorReference="anchorPosition"
        onClose={handleCloseMergeParticpantPopover}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 1 }}>
          <Box sx={{ paddingX: '5px', marginTop: '5px' }}>
            <Typography sx={{ color: '#6B6B6B', marginBottom: '10px', fontWeight: 'bold' }}>
              With
            </Typography>
            <Box>
              {participantList.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    padding: '8px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    '&:hover': { backgroundColor: '#f2f2f2' },
                  }}
                >
                  <Typography
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '24px',
                      width: '24px',
                      backgroundColor: '#7d5ae8',
                      borderRadius: '24px',
                      color: '#ffffff',
                    }}
                  >
                    <PersonOutlineOutlinedIcon fontSize="12" color="white" />
                  </Typography>
                  <Typography>{item.name}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Popover>
      <Popover
        id={openCreateParticipantPopoverId}
        open={openCreateParticipantPopover}
        anchorPosition={newParticipantAnchor}
        anchorReference="anchorPosition"
        onClose={handleCloseNewParticipantPopover}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 1 }}>
          <Box sx={{ paddingX: '5px', marginTop: '5px' }}>
            <Typography sx={{ color: '#6B6B6B', marginBottom: '10px', fontWeight: 'bold' }}>
              Participant
            </Typography>
            <Box>
              <form onSubmit={handleAddNewParticipant}>
                <FormControl sx={{ width: '25ch' }}>
                  <FormLabel>Name</FormLabel>
                  <TextField
                    variant="outlined"
                    placeholder="Participant Name"
                    helperText={isEmptyParticipantName ? 'This field is mandatory' : ''}
                    error={isEmptyParticipantName}
                    size="small"
                    fullWidth
                    value={participantName}
                    onChange={(e) => {
                      setParticipantName(e.target.value);
                      setIsEmptyParticipantName(false);
                    }}
                  />
                </FormControl>
                <Stack sx={{ marginY: '10px' }}>
                  {' '}
                  <Button
                    disabled={isAddingNewParticipant}
                    sx={{ cursor: 'pointer' }}
                    type="submit"
                    variant="contained"
                  >
                    {isAddingNewParticipant ? 'Saving' : 'Save'}
                  </Button>
                </Stack>
              </form>
            </Box>
          </Box>
        </Box>
      </Popover>

      <AssignTags
        id={id}
        onClose={handleCloseAssignTagModal}
        open={openAssignTagModal}
        tags={tagsList}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        isFetchingTags={isFetchingTags}
        speakerId={currentSpeaker?.speaker_id}
        transcriptId={transcriptId}
        addStudyTag={addStudyTag}
        isAddingStudyTag={isAddingStudyTag}
        canEdit={canEdit}
        assigned_tags={previouslyAssignedTags}
      />
      <DeleteConfirmationDialog
        open={open}
        onClose={handleClose}
        onConfirm={handleConfirm}
        title="Unassign Tag"
        isPending={isPending}
        content={`This action cannot be undone. Are you sure want to unassign this Tag ?`}
        okText={isPending ? 'Unassigning' : 'Unassign Tag'}
      />
    </>
  );
}

export default EditInterviewer;
